
import { useEffect, useRef } from 'react';
import { useCart } from '../context/CartContext';

export interface QuickViewProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  image?: string;
  type: 'class' | 'kit' | 'package' | 'manual' | 'bundle';
  badge?: string;
  details?: {
    label: string;
    value: string;
    icon: string;
  }[];
  extras?: string[];
}

interface QuickViewModalProps {
  product: QuickViewProduct | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function QuickViewModal({ product, isOpen, onClose }: QuickViewModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const { addToCart } = useCart();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      type: product.type,
      description: product.description,
    });
    onClose();
  };

  if (!isOpen || !product) return null;

  const savings = product.originalPrice ? product.originalPrice - product.price : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"></div>

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden animate-scale-in"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center cursor-pointer hover:bg-stone-100 transition-colors"
        >
          <i className="ri-close-line text-xl text-stone-600"></i>
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Image Section */}
          <div className="relative w-full md:w-2/5 h-64 md:h-auto bg-gradient-to-br from-amber-50 to-stone-100">
            {product.badge && (
              <div className="absolute top-4 left-4 px-4 py-1.5 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 text-xs font-bold rounded-full uppercase tracking-wider shadow-lg">
                {product.badge}
              </div>
            )}
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover md:object-contain p-6"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-300 flex items-center justify-center">
                  <i className={`text-4xl text-amber-900 ${
                    product.type === 'class' ? 'ri-graduation-cap-line' :
                    product.type === 'kit' ? 'ri-box-3-line' :
                    product.type === 'manual' ? 'ri-book-open-line' :
                    product.type === 'package' ? 'ri-gift-line' :
                    'ri-stack-line'
                  }`}></i>
                </div>
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="flex-1 p-8 md:p-10 overflow-y-auto max-h-[60vh] md:max-h-[80vh]">
            {/* Type Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-100 rounded-full mb-4">
              <i className={`text-sm text-amber-600 ${
                product.type === 'class' ? 'ri-graduation-cap-line' :
                product.type === 'kit' ? 'ri-box-3-line' :
                product.type === 'manual' ? 'ri-book-open-line' :
                product.type === 'package' ? 'ri-gift-line' :
                'ri-stack-line'
              }`}></i>
              <span className="text-xs font-medium text-stone-600 uppercase tracking-wider">
                {product.type}
              </span>
            </div>

            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-3">
              {product.name}
            </h2>

            {/* Description */}
            <p className="text-stone-500 leading-relaxed mb-6">
              {product.description}
            </p>

            {/* Details */}
            {product.details && product.details.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                {product.details.map((detail, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl"
                  >
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                      <i className={`${detail.icon} text-amber-600`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-stone-400">{detail.label}</p>
                      <p className="text-sm font-semibold text-stone-700">{detail.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Extras */}
            {product.extras && product.extras.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Includes</p>
                <div className="flex flex-wrap gap-2">
                  {product.extras.map((extra, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full"
                    >
                      <i className="ri-check-line"></i>
                      {extra}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Price & CTA */}
            <div className="flex items-center justify-between pt-6 border-t border-stone-100">
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl font-bold text-stone-900">${product.price}</span>
                  {product.originalPrice && (
                    <span className="text-lg text-stone-300 line-through">${product.originalPrice}</span>
                  )}
                </div>
                {savings > 0 && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">
                    Save ${savings}
                  </span>
                )}
              </div>
              <button
                onClick={handleAddToCart}
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 font-bold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-[1.02]"
              >
                <i className="ri-shopping-cart-line"></i>
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
